import { useQuery } from '@tanstack/react-query';
import { getCategories, getCategoriesGroup, getCategoriesSubgroup } from '@/api/category';
import { ReactQueryKey } from '@/constants/ReactQueryKeys';
import type { CategoriesGroup, Category } from '@/types/Category';
import type { QueryParamsInterface } from '@/types/Shared';

export const useCategories = ({
  onSuccess,
  onError,
  params,
}: {
  onSuccess?: (data: any) => void;
  onError?: (err: any) => void;
  params?: QueryParamsInterface;
}) => {
  //Tanstack: version 5 query implementation
  return useQuery({
    queryKey: [ReactQueryKey.GET_CATEGORIES, params],
    queryFn: () => getCategories({ ...params }),
    onSuccess,
    onError,
    enabled: params && !!params,
    keepPreviousData: true,
    staleTime: 30000,
  });
};

export const useCategoryGroup = ({ onSuccess, onError }: { onSuccess?: (data: any) => void; onError?: (err: any) => void }) => {
  //Tanstack: version 5 query implementation
  return useQuery({
    queryKey: ['get-categoriesGroup'],
    queryFn: getCategoriesGroup,
    onSuccess,
    onError,
    keepPreviousData: true,
    staleTime: 30000,
  });
};

export const useCategorySubGroup = ({
  onSuccess,
  onError,
  group,
}: {
  onSuccess?: (data: any) => void;
  onError?: (err: any) => void;
  group?: CategoriesGroup;
}) => {
  //Tanstack: version 5 query implementation
  return useQuery({
    queryKey: ['get-categoriesSubGroup', group],
    queryFn: () => getCategoriesSubgroup({ groupId: group?.id }),
    onSuccess,
    onError,
    enabled: group === undefined || !!group,
    keepPreviousData: true,
    staleTime: 30000,
  });
};

interface PhaseCategoriesHookProps {
  phase: string;
}

export const usePhaseCategories = ({ phase }: PhaseCategoriesHookProps) => {
  const fetchCategoriesSubGroup = useCategorySubGroup({});
  const categoriesSubGroup = fetchCategoriesSubGroup?.data?.data?.find((cat: CategoriesGroup) => cat.name === phase.toLowerCase());
  const fetchPhaseCategories = useCategories({
    params: { subGroupId: categoriesSubGroup?.id },
  });
  return fetchPhaseCategories;
};

interface CategoryByIdHookProps {
  categoryId: number;
}

export const useCategoryById = ({ categoryId }: CategoryByIdHookProps) => {
  const fetchCategories = useCategories({});
  const category = fetchCategories?.data?.data?.find((cat: Category) => cat.id === categoryId);
  return category;
};
